import { Link } from "react-router-dom";
import { Code, Globe, Lightbulb, ArrowUpRight } from "lucide-react";

/**
 * "Våra tjänster" on the homepage.
 * Layout: intro on the left (sticky on large screens), the services as one
 * panel of clickable rows on the right. Same layout on phones, stacked, so all
 * three services are visible without swiping.
 */
const services = [
  {
    icon: Code,
    title: "Skräddarsydd mjukvara",
    description:
      "Vi designar och utvecklar skräddarsydda mjukvarusystem, appar och program. Gå från idé till färdig produkt, snabbt och kostnadseffektivt.",
    action: "Mer info",
    link: "/services#development",
  },
  {
    icon: Globe,
    title: "Hemsida + Lokal SEO",
    description:
      "Professionella hemsidor för lokala företag med snabb leverans med lokal SEO-optimering som tillval.",
    action: "Mer info",
    link: "/services#webseo",
  },
  {
    icon: Lightbulb,
    title: "Rådgivning",
    description: "Vi erbjuder rådgivning kring AI-verktyg och digital strategi.",
    action: "Mer info",
    link: "/services#consulting",
  },
];

const ServicesOverview = () => {
  return (
    <section id="services" className="bg-background py-14 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Intro */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
                Våra tjänster
              </h2>
              <p className="mt-3 md:mt-5 max-w-md text-base md:text-lg leading-relaxed text-muted-foreground">
                Mjukvara, webb och rådgivning — skräddarsytt för er verksamhet.
              </p>
              <div className="mt-6 hidden lg:block h-1 w-16 rounded-full bg-accent" aria-hidden="true" />
            </div>
          </div>

          {/* Service rows */}
          <ul className="lg:col-span-8 overflow-hidden rounded-3xl bg-white ring-1 ring-border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-12px_rgba(16,24,40,0.12)]">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <li key={service.title} className="border-b border-border last:border-b-0">
                  <Link
                    to={service.link}
                    aria-label={`${service.title} – ${service.action}`}
                    className="group grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1fr_auto] items-start gap-4 sm:gap-6 p-5 sm:p-7 md:p-8
                      transition-colors duration-200 hover:bg-secondary/60
                      focus-visible:outline-none focus-visible:bg-secondary/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
                  >
                    <span
                      className="flex h-12 w-12 md:h-14 md:w-14 items-center justify-center rounded-2xl bg-[#FFC300] text-[#2A3A4A]
                        transition-transform duration-200 group-hover:-rotate-6"
                      aria-hidden="true"
                    >
                      <Icon className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2} />
                    </span>

                    <span className="min-w-0">
                      <span className="flex items-center justify-between gap-3">
                        <span className="text-lg md:text-2xl font-semibold tracking-tight text-foreground">
                          {service.title}
                        </span>
                        {/* Phones: compact arrow next to the title */}
                        <ArrowUpRight
                          className="sm:hidden h-5 w-5 shrink-0 text-foreground/50"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="mt-2 block max-w-xl text-[15px] md:text-base leading-relaxed text-muted-foreground">
                        {service.description}
                      </span>
                    </span>

                    {/* Larger screens: label + arrow that fills yellow on hover */}
                    <span className="hidden sm:flex items-center gap-3 self-center text-sm font-medium text-foreground/70 group-hover:text-foreground">
                      {service.action}
                      <span
                        className="flex h-11 w-11 items-center justify-center rounded-full ring-1 ring-border
                          transition-all duration-200 group-hover:bg-[#FFC300] group-hover:ring-[#FFC300] group-hover:text-[#2A3A4A]"
                        aria-hidden="true"
                      >
                        <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;
